<template>
  <div class="my-10">
    <h2 class="text-header2 md:text-desktop-header2">
      {{ slice.primary.title }}
    </h2>
    <PrismicRichText
      :field="slice.primary.description"
      class="mt-10 pr-20 text-body-copy md:text-desktop-body-copy"
    />

    <!-- Netlify Form -->
    <div v-if="!formSubmitted">
      <!-- Changes to this form need to be reflected on the Static Version under /public/forms/contact.html -->
      <form
        class="mt-10"
        name="Contact Form"
        method="POST"
        data-netlify="true"
        netlify-honeypot="bot-field"
        @submit.prevent="handleSubmit"
      >
        <div class="grid gap-8">
          <input
            name="form-name"
            value="Contact Form"
            type="hidden"
          />

          <p class="hidden">
            <label>
              Don’t fill this out if you’re human: <input name="bot-field" />
            </label>
          </p>

          <div class="md:flex md:gap-8">
            <input
              class="w-full appearance-none border-0 border-b-2 border-charcoal-100/50 bg-transparent p-3 leading-tight text-charcoal-100 placeholder:text-charcoal-100/50 focus:border-charcoal-100 focus:outline-none md:w-[100px]"
              name="title"
              type="text"
              placeholder="Title"
              autocomplete="off"
              required
            />

            <input
              class="w-full appearance-none border-0 border-b-2 border-charcoal-100/50 bg-transparent p-3 leading-tight text-charcoal-100 placeholder:text-charcoal-100/50 focus:border-charcoal-100 focus:outline-none"
              name="first_name"
              type="text"
              placeholder="First Name"
              autocomplete="off"
              required
            />
          </div>

          <input
            class="w-full appearance-none border-0 border-b-2 border-charcoal-100/50 bg-transparent p-3 leading-tight text-charcoal-100 placeholder:text-charcoal-100/50 focus:border-charcoal-100 focus:outline-none"
            name="last_name"
            type="text"
            placeholder="Last Name"
            autocomplete="off"
            required
          />

          <input
            class="inline-block w-full appearance-none border-0 border-b-2 border-charcoal-100/50 bg-transparent p-3 leading-tight text-charcoal-100 placeholder:text-charcoal-100/50 focus:border-charcoal-100 focus:outline-none md:col-span-2"
            name="email"
            type="text"
            placeholder="Email"
            autocomplete="off"
            required
          />

          <input
            class="w-full appearance-none border-0 border-b-2 border-charcoal-100/50 bg-transparent p-3 leading-tight text-charcoal-100 placeholder:text-charcoal-100/50 focus:border-charcoal-100 focus:outline-none"
            name="subject"
            type="text"
            placeholder="Subject Line"
            autocomplete="off"
            required
          />

          <select
            name="department"
            required
            class="w-full appearance-none border-0 border-b-2 border-charcoal-100/50 bg-transparent p-3 leading-tight text-charcoal-100 placeholder:text-charcoal-100/50 focus:border-charcoal-100 focus:outline-none"
          >
            <option
              value=""
              selected
              disabled
            >
              Department
            </option>
            <option value="exhibitions">
              Exhibitions
            </option>
            <option value="learning">
              Learning & Engagement
            </option>
            <option value="operations">
              Operations
            </option>
          </select>
        </div>

        <textarea
          name="message"
          placeholder="Message"
          rows="4"
          cols="50"
          required
          class="mt-4 w-full appearance-none border-0 border-b-2 border-charcoal-100/50 bg-transparent p-3 leading-tight text-charcoal-100 placeholder:text-charcoal-100/50 focus:border-charcoal-100 focus:outline-none"
        />

        <button
          class="mt-10 inline-block rounded-sm border border-charcoal-100 bg-charcoal-100 px-3.5 py-1.5 text-body-copy  text-white transition-colors hover:bg-charcoal-25"
          type="submit"
        >
          Submit
        </button>
      </form>
    </div>

    <!-- Thank you Message -->
    <div
      v-else
      class="mt-10"
    >
      <h2 class="text-header1 md:text-desktop-header4">
        Thank you for your contact.
      </h2>
      <p class="mt-2">
        We will get back to you as soon as possible.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Content } from "@prismicio/client";

// The array passed to `getSliceComponentProps` is purely optional.
// Consider it as a visual hint for you when templating your slice.
const props = defineProps(
  getSliceComponentProps<Content.ContactFormSlice>([
    "slice",
    "index",
    "slices",
    "context",
  ]),
);

const formSubmitted = ref(false);

const handleSubmit = (event: Event) => {
  event.preventDefault();

  const form = event.target as HTMLFormElement;
  const formData = new FormData(form);
  const formObject = Object.fromEntries(formData.entries());

  // Post to static asset - https://answers.netlify.com/t/support-guide-form-problems-form-debugging-404-when-submitting/92
  fetch("/forms/contact.html", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(formObject as never).toString(),
  })
    .then(() => {
      formSubmitted.value = true;
    })
    .catch((error: unknown) => {
      alert(error);
    });
};
</script>
